<template>
  <div>
    <label class="block text-lg font-semibold mb-2">{{ title }}</label>
    <div class="flex gap-3">
      <div class="col-12 md:col-6 py-1 px-0" style="width: 23rem">
        <div
          class="campaign-card"
          :class="{ 'campaign-card--selected': campaigns.promotion.enabled }"
          @click="toggleCampaign('promotion')"
        >
          <div class="campaign-card__header">
            <div class="campaign-card__icon">
              <i
                class="pi pi-megaphone"
                style="color: var(--primary-color)"
              ></i>
            </div>
            <div class="campaign-card__title">
              <h5 class="m-0 text-lg font-semibold">Promotion</h5>
              <Checkbox
                v-model="promotionEnabled"
                :binary="true"
                class="campaign-card__checkbox"
                @click.stop
              />
            </div>
          </div>
          <div
            v-if="campaigns.promotion.enabled && !hideIntervalInputs"
            class="campaign-card__content"
            @click.stop
          >
            <label
              for="promotion-interval"
              class="block text-sm font-medium mb-2"
            >
              Promotions Per Interval
            </label>
            <InputNumber
              id="promotion-interval"
              v-model="promotionInterval"
              :min="0"
              :minFractionDigits="1"
              placeholder="Enter interval value"
              class="w-full"
              :class="{
                'p-invalid':
                  showError &&
                  campaigns.promotion.enabled &&
                  (!campaigns.promotion.interval ||
                    campaigns.promotion.interval < 0.1),
              }"
              @click.stop
            />
          </div>
          <div
            v-else-if="campaigns.promotion.enabled"
            class="campaign-card__content"
            @click.stop
          >
            <small class="text-color-secondary"
              >Custom monthly interval schedule applies below</small
            >
          </div>
        </div>
      </div>

      <div class="col-12 md:col-6 py-1 px-0" style="max-width: 23rem">
        <div
          class="campaign-card"
          :class="{ 'campaign-card--selected': campaigns.spotify.enabled }"
          @click="toggleCampaign('spotify')"
        >
          <div class="campaign-card__header">
            <div class="campaign-card__icon">
              <i class="pi pi-play" style="color: var(--primary-color)"></i>
            </div>
            <div class="campaign-card__title">
              <h5 class="m-0 text-lg font-semibold">Spotify</h5>
              <Checkbox
                v-model="spotifyEnabled"
                :binary="true"
                class="campaign-card__checkbox"
                @click.stop
              />
            </div>
          </div>
          <div
            v-if="campaigns.spotify.enabled && !hideIntervalInputs"
            class="campaign-card__content"
            @click.stop
          >
            <label
              for="spotify-interval"
              class="block text-sm font-medium mb-2"
            >
              Promotions Per Interval
            </label>
            <InputNumber
              id="spotify-interval"
              v-model="spotifyInterval"
              :min="0.1"
              :minFractionDigits="1"
              :maxFractionDigits="2"
              placeholder="Enter interval value"
              class="w-full"
              :class="{
                'p-invalid':
                  showError &&
                  campaigns.spotify.enabled &&
                  (!campaigns.spotify.interval ||
                    campaigns.spotify.interval < 0.1),
              }"
              @click.stop
            />
          </div>
          <div
            v-else-if="campaigns.spotify.enabled"
            class="campaign-card__content"
            @click.stop
          >
            <small class="text-color-secondary"
              >Custom monthly interval schedule applies below</small
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import Checkbox from "primevue/checkbox";
import InputNumber from "primevue/inputnumber";

// eslint-disable-next-line no-undef
const props = defineProps({
  campaigns: {
    type: Object,
    required: true,
  },
  showError: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Campaign Types",
  },
  hideIntervalInputs: {
    type: Boolean,
    default: false,
  },
});

// eslint-disable-next-line no-undef
const emit = defineEmits(["update:campaigns"]);

const toggleCampaign = (type) => {
  const updated = { ...props.campaigns };
  updated[type].enabled = !updated[type].enabled;
  if (!updated[type].enabled) {
    updated[type].interval = null;
  }
  emit("update:campaigns", updated);
};

const promotionEnabled = computed({
  get: () => props.campaigns.promotion.enabled,
  set: (value) => {
    const updated = { ...props.campaigns };
    updated.promotion.enabled = value;
    if (!value) {
      updated.promotion.interval = null;
    }
    emit("update:campaigns", updated);
  },
});

const promotionInterval = computed({
  get: () => props.campaigns.promotion.interval,
  set: (value) => {
    const updated = { ...props.campaigns };
    updated.promotion.interval = value;
    emit("update:campaigns", updated);
  },
});

const spotifyEnabled = computed({
  get: () => props.campaigns.spotify.enabled,
  set: (value) => {
    const updated = { ...props.campaigns };
    updated.spotify.enabled = value;
    if (!value) {
      updated.spotify.interval = null;
    }
    emit("update:campaigns", updated);
  },
});

const spotifyInterval = computed({
  get: () => props.campaigns.spotify.interval,
  set: (value) => {
    const updated = { ...props.campaigns };
    updated.spotify.interval = value;
    emit("update:campaigns", updated);
  },
});
</script>

<style scoped>
.campaign-card {
  background: var(--surface-card);
  border: 2px solid var(--surface-border);
  border-radius: var(--border-radius);
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-height: 120px;
}

.campaign-card:hover {
  box-shadow: var(--shadow-2);
  transform: translateY(-2px);
}

.campaign-card--selected {
  border-color: var(--primary-color);
}

.campaign-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.campaign-card__icon {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-100);
  border: 2px solid var(--surface-200);
  font-size: 1.25rem;
}

.campaign-card__title {
  flex: 1;
  margin-left: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.campaign-card__checkbox {
  pointer-events: none;
}

.campaign-card__content {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-border);
}

.campaign-card--selected .campaign-card__content {
  border-top-color: var(--primary-200);
}
</style>
